import logger from '@achieve/cx-logger'
import GraphQLService from './GraphQLService'
import { removeExtraSpaces } from '../common/helpers'
import {
  isRenderablePageModel,
  mapJsonLdSchemas,
  mapPageDataGql,
  PAGE_MODEL_TYPENAMES,
} from './mappers/mapPageDataGql'
import { mapGeoCitiesGql } from './mappers/mapGeoCitiesGql'
import { WWW_PAGE_MODEL_FIELDS } from './pageData/graphqlFields'
import { RICH_TEXT_EMBEDDED_ENTRY_FRAGMENT } from './pageData/richTextLinksFields'
import {
  buildPageModelSupplementQuery,
  PAGE_MODELS_WITH_SUPPLEMENT,
} from './pageData/supplementFields'
import { isUnresolvedLinkError, loggedGraphqlQuery } from './loggedGraphqlQuery'

export const CONTENTFUL_GRAPHQL_MAX_QUERY_BYTES = 8 * 1024

function publishedModelSelection() {
  return PAGE_MODEL_TYPENAMES.map(
    (typename) => `... on ${typename} { sys { id } }`
  ).join(' ')
}

export function buildPageByPathQuery() {
  return removeExtraSpaces(`
      query PageByPath($preview: Boolean, $path: String!) {
        page: wwwPagesCollection(
          preview: $preview
          limit: 1
          where: { path: $path }
        ) {
          items {
            path
            locale
            template
            updatedAt
            isRestricted
            restrictionValue
            sys { id publishedAt }
            category {
              slug
              title
              sys { id }
            }
            jsonLdSchemasCollection(preview: $preview) {
              items {
                ... on SchemaEntity {
                  jsonld
                  slug
                  sys { id }
                }
              }
            }
            model {
              ${WWW_PAGE_MODEL_FIELDS}
            }
          }
        }
      }
      ${RICH_TEXT_EMBEDDED_ENTRY_FRAGMENT}
    `)
}

export default class PageDataService extends GraphQLService {
  async fetchModelSupplement(model, preview = false) {
    const typename = model?.__typename
    const modelId = model?.sys?.id

    if (
      !typename ||
      !modelId ||
      !PAGE_MODELS_WITH_SUPPLEMENT.includes(typename)
    ) {
      return null
    }

    const query = buildPageModelSupplementQuery(typename)
    if (!query) {
      return null
    }

    const data = await loggedGraphqlQuery(
      'PageDataService.fetchModelSupplement',
      () => this.run(query, preview, { id: modelId }),
      { context: { typename, modelId, preview } }
    )

    return data?.model ?? null
  }

  async getPageByPath(path, preview = false) {
    const query = buildPageByPathQuery()

    const data = await loggedGraphqlQuery(
      'PageDataService.getPageByPath',
      () => this.run(query, preview, { path }),
      {
        context: { path, preview },
        countItems: (d) => d?.page?.items?.length ?? null,
      }
    )
    const item = data?.page?.items?.[0]
    const supplement = await this.fetchModelSupplement(item?.model, preview)
    const page = mapPageDataGql(
      supplement ? { ...item, model: { ...item.model, ...supplement } } : item
    )

    if (!page) {
      throw new Error('PageDataService: page not found or unmappable')
    }

    return page
  }

  /** Resolve wwwPages.path from linked model entry id (preview API). */
  async getPagePathByModelId(modelId, preview = false) {
    const query = removeExtraSpaces(`
      query PagePathByModelId($preview: Boolean, $modelId: String!) {
        page: wwwPagesCollection(
          preview: $preview
          limit: 1
          where: { model: { sys: { id: $modelId } } }
        ) {
          items { path }
        }
      }
    `)

    const data = await loggedGraphqlQuery(
      'PageDataService.getPagePathByModelId',
      () => this.run(query, preview, { modelId }),
      {
        context: { modelId, preview },
        countItems: (d) => d?.page?.items?.length ?? null,
      }
    )

    return data?.page?.items?.[0]?.path ?? null
  }

  /**
   * Published wwwPages paths whose linked model also resolves on the Delivery API.
   * A published wrapper can still point at an unpublished model; that page 404s.
   * Unresolved-link errors are ignored so one draft model does not drop the rest.
   */
  async getPublishedPaths(paths = []) {
    const uniquePaths = [...new Set(paths.filter(Boolean))]

    if (!uniquePaths.length) {
      return []
    }

    const query = removeExtraSpaces(`
      query PublishedPaths($preview: Boolean, $paths: [String!]!) {
        page: wwwPagesCollection(
          preview: $preview
          limit: ${uniquePaths.length}
          where: { path_in: $paths }
        ) {
          items {
            path
            model {
              __typename
              ${publishedModelSelection()}
            }
          }
        }
      }
    `)
    const context = { pathCount: uniquePaths.length }

    logger.debug(
      '[PageDataService.getPublishedPaths] Contentful GraphQL request',
      context
    )

    const response = await this.run(query, false, { paths: uniquePaths })
    const errors = response?.data?.errors || []
    const blockingErrors = errors.filter(
      (error) => !isUnresolvedLinkError(error)
    )

    if (blockingErrors.length) {
      logger.error(
        '[PageDataService.getPublishedPaths] Contentful GraphQL failed',
        { ...context, graphqlErrors: errors }
      )
      throw new Error(
        blockingErrors[0]?.message ||
          'PageDataService.getPublishedPaths: GraphQL error'
      )
    }

    if (errors.length) {
      logger.warn(
        '[PageDataService.getPublishedPaths] ignored unresolved model links',
        { ...context, unresolvedLinkCount: errors.length }
      )
    }

    const publishedPaths = (response?.data?.data?.page?.items || [])
      .filter((item) => item?.path && isRenderablePageModel(item.model))
      .map((item) => item.path)

    logger.info(
      '[PageDataService.getPublishedPaths] Contentful GraphQL fetched',
      { ...context, itemCount: publishedPaths.length }
    )

    return publishedPaths
  }

  async getPasswordProtectionByPageId(entryId) {
    const query = removeExtraSpaces(`
      query PagePasswordProtection($entryId: String!) {
        page: wwwPagesCollection(limit: 1, where: { sys: { id: $entryId } }) {
          items {
            sys { id }
            isRestricted
            restrictionValue
          }
        }
      }
    `)

    const data = await loggedGraphqlQuery(
      'PageDataService.getPasswordProtectionByPageId',
      () => this.run(query, false, { entryId }),
      {
        context: { entryId },
        countItems: (result) => result?.page?.items?.length ?? null,
      }
    )
    const page = data?.page?.items?.[0]

    if (!page?.sys?.id) {
      return null
    }

    return {
      entryId: page.sys.id,
      isRestricted: page.isRestricted === true,
      restrictionValue: page.restrictionValue ?? null,
    }
  }

  /** JSON-LD envelope only — no REST fallback. */
  async getJsonLdEnvelopeByPath(path, preview = false) {
    const query = removeExtraSpaces(`
      query PageJsonLdByPath($preview: Boolean, $path: String!) {
        page: wwwPagesCollection(
          preview: $preview
          limit: 1
          where: { path: $path }
        ) {
          items {
            jsonLdSchemasCollection(preview: $preview) {
              items {
                ... on SchemaEntity {
                  jsonld
                  slug
                  sys { id }
                }
              }
            }
          }
        }
      }
    `)

    const data = await loggedGraphqlQuery(
      'PageDataService.getJsonLdEnvelopeByPath',
      () => this.run(query, preview, { path }),
      {
        context: { path, preview },
        countItems: (d) => d?.page?.items?.length ?? null,
      }
    )
    const item = data?.page?.items?.[0]

    if (!item) {
      return null
    }

    const jsonLdSchemas = mapJsonLdSchemas(item.jsonLdSchemasCollection?.items)

    return jsonLdSchemas ? { jsonLdSchemas } : null
  }

  /**
   * City list for a geo state — replaces REST fillModel for wwwDebtConsolidationStatesCities.
   * @param {string} state
   * @param {boolean} [preview=false]
   * @returns {Promise<{ state: object | null, cities: object[] }>}
   */
  async getGeoCitiesByState(state, preview = false) {
    const query = removeExtraSpaces(`
      query GeoCitiesByState($preview: Boolean, $state: String!) {
        geo: wwwDebtConsolidationStatesCitiesCollection(
          preview: $preview
          order: city_ASC
          where: { state: $state }
        ) {
          items {
            state
            city
            slug
            sys { id publishedAt }
            linkedFrom {
              wwwPagesCollection(limit: 1) {
                items { updatedAt }
              }
            }
          }
        }
      }
    `)

    const data = await loggedGraphqlQuery(
      'PageDataService.getGeoCitiesByState',
      () => this.run(query, preview, { state }),
      {
        context: { state, preview },
        countItems: (d) => d?.geo?.items?.length ?? null,
      }
    )
    const items = data?.geo?.items ?? []

    return mapGeoCitiesGql(items)
  }
}
